'use client';

import { AddressInput } from '#/components/ethereum/address-input';
import { Alert, AlertDescription, AlertTitle } from '#/components/ui/alert';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/components/ui/tooltip';
import { useTranslation } from '#/contexts/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { ethers } from 'ethers';
import { AlertTriangle, ExternalLink, HelpCircle, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useContext, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { StakingValidatorsInput } from './components/staking-validators-input';
import { MachineContext } from './contexts/staking-machine';
import { StakingDialogInformationConstraintDialog } from './staking-dialog-information-constraint-dialog';

export function StakingDialogInformation() {
  const translationApplication = useTranslation(
    translation => translation.Application
  );
  const translation = useTranslation(
    translation => translation.EthereumStaking.StakingAction
  );

  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const { count, withdrawaddr, claimaddr } = state.context;

  const formSchema = useMemo(
    () =>
      z.object({
        count: z
          .number({
            required_error: 'Please enter the number of validators',
            invalid_type_error: 'Please enter the number of validators',
          })
          .int()
          .min(1, translation.Information.Validators.MinErrorMessage)
          .max(100, translation.Information.Validators.MaxErrorMessage),
        withdrawaddr: z
          .string({
            required_error: 'Withdrawal address is required',
          })
          .refine(val => ethers.utils.isAddress(val), {
            message: translation.Information.WithdrawalAddress.ErrorMessage,
          }),
        claimaddr: z
          .string({
            required_error: 'Claim address is required',
          })
          .refine(val => ethers.utils.isAddress(val), {
            message: translation.Information.ClaimAddress.ErrorMessage,
          }),
      }),
    [translation]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      count: count ?? 1,
      withdrawaddr: withdrawaddr ?? '',
      claimaddr: claimaddr ?? '',
    },
  });

  const isManualSetRef = useRef(new Set<string>());
  const [isManualChangedDialogOpen, setIsManualChangedDialogOpen] =
    useState(false);

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (isManualSetRef.current.size > 0) {
      // show confirm dialog
      setIsManualChangedDialogOpen(true);
      return;
    }
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { count, withdrawaddr, claimaddr } = values;
    dispatch({
      type: 'NEXT',
      data: {
        count,
        claimaddr,
        withdrawaddr,
      },
    });
  }

  function onConfirm() {
    isManualSetRef.current.clear();

    form.handleSubmit(onSubmit)();
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{translation.Heading}</DialogTitle>
        <DialogDescription>
          {translation.Information.Description}
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="count"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>
                    {translation.Information.Validators.Label}
                  </FormLabel>
                  <FormDescription>
                    <Link
                      href="https://app.bedrock.technology/unieth"
                      target="_blank"
                      prefetch={false}
                      className="hover:underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1 inline-block" />
                      {translation.Information.UniETHLinkLabel}
                    </Link>
                  </FormDescription>
                </div>
                <FormControl>
                  <StakingValidatorsInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="withdrawaddr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {translation.Information.WithdrawalAddress.Label}
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="w-4 h-4 inline-block ml-2" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {translation.Information.WithdrawalAddress.LabelHelp}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </FormLabel>
                <FormControl>
                  <AddressInput
                    useConnectedWallet
                    isManualSetRef={isManualSetRef}
                    placeholder={
                      translation.Information.WithdrawalAddress.Placeholder
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="claimaddr"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {translation.Information.ClaimAddress.Label}
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="w-4 h-4 inline-block ml-2" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{translation.Information.ClaimAddress.LabelHelp}</p>
                    </TooltipContent>
                  </Tooltip>
                </FormLabel>
                <FormControl>
                  <AddressInput
                    useConnectedWallet
                    isManualSetRef={isManualSetRef}
                    placeholder={
                      translation.Information.ClaimAddress.Placeholder
                    }
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Alert className="bg-yellow-50">
            <AlertTriangle className="w-4 h-5" />
            <AlertTitle>{translation.Information.Warning.Title}</AlertTitle>
            <AlertDescription>
              {translation.Information.Warning.Description}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                {translationApplication.Dialog.CancelButtonLabel}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="primary"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {translation.Information.ContinueButtonLabel}
            </Button>
          </DialogFooter>
        </form>
      </Form>

      <StakingDialogInformationConstraintDialog
        open={isManualChangedDialogOpen}
        onOpenChange={setIsManualChangedDialogOpen}
        onConfirm={onConfirm}
      />
    </>
  );
}
